import { ApiFile } from "@/types/common.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiEsignResult } from "@/types/esign.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { PermissionType } from "@/types/role.types.ts";
import { Rule, RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";

export enum ApiFolderType {
  employee = "employee",
  organization = "organization",
}

export const folderParamKeys: Record<ApiFolderType, string> = {
  [ApiFolderType.organization]: "org_folder_id",
  [ApiFolderType.employee]: "folder_id",
};

export interface ApiFolderShort {
  id: number;
  title: string;
  type: ApiFolderType;
}

export interface ApiFolderAcl {
  can_edit: boolean;
  can_delete: boolean;
  can_edit_documents: boolean;
  can_delete_documents: boolean;
}

export interface ApiFolder extends ApiFolderShort {
  document_count: number;
  acl: ApiFolderAcl;
  view_documents_rule_groups?: RuleGroup[] | null;
  edit_documents_rule_groups?: RuleGroup[] | null;
  view_documents_rules?: Rule[] | null;
  edit_documents_rules?: Rule[] | null;
}

export interface ApiFolderPayload {
  title: string;
  type?: ApiFolderType;
  view_documents_rule_groups: RuleGroupShort[];
  edit_documents_rule_groups: RuleGroupShort[];
}

export function getDocumentFolderRuleGroups(
  folder: ApiFolder | null,
  field: "view_documents_rules" | "edit_documents_rules"
): RuleGroup[] {
  const groups =
    folder?.[
      field === "view_documents_rules" ? "view_documents_rule_groups" : "edit_documents_rule_groups"
    ];
  if (groups != null) return groups;
  const rules = folder?.[field] ?? [];
  return rules.length > 0 ? [{ rules }] : [];
}

export interface ApiDocumentsFilter extends ApiFilterBase {
  folder_id?: number;
  folder_type?: ApiFolderType;
  has_esign_markup?: boolean;
  employee_id?: number;
  title?: string;
}

export interface ApiDocumentAcl {
  can_edit: boolean;
  can_delete: boolean;
}

export enum ApiDocumentPreviewStatus {
  pending = "pending",
  ready = "ready",
  failed = "failed",
}

export interface ApiDocumentPreview {
  status: ApiDocumentPreviewStatus;
  file: ApiFile | null;
}

export enum ApiEsignMarkupStatus {
  draft = "draft",
  ready = "ready",
}

export interface ApiEsignEnvelopesProgress {
  completed_count: number;
  active_count: number;
  total_count: number;
}

export interface ApiDocument {
  id: number;
  title: string;
  folder: ApiFolderShort;
  datetime_updated_at: Date;
  uploaded_by: ApiEmployee;
  file?: ApiFile;
  preview: ApiDocumentPreview;
  acl: ApiDocumentAcl;
  esign_markup_status: ApiEsignMarkupStatus | null;
  esign_envelopes_progress: ApiEsignEnvelopesProgress | null;
  esign_result: ApiEsignResult | null;
  can_sign: boolean;
  can_acknowledge: boolean;
}

export interface ApiDocumentSavePayload {
  title: string;
  folder_id: number;
  file_uuid: string;
}

export interface ApiFolderRoles {
  folder_id: number;
  items: ApiFolderRolesItem[];
}

export interface ApiFolderRolesItem {
  role_id: number;
  permission_value: PermissionType;
}
